import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { clsx } from 'clsx'

import styles from './TurnPreferenceResultsPage.module.scss'

import {
  exportMeetingAttributionTurnPreferenceVotes,
  getMeetingAttributionTurnPreferenceResults,
  type TurnPreferenceBreakdownRow,
  type TurnPreferenceTally,
} from '@/api/internal'
import Button from '@/components/Button'
import { downloadBlob } from '@/utils/csv'

const PICKED_BY_LABELS: Record<string, string> = {
  speaker: 'Speakers',
  wer: 'Words',
}

const percent = (value: number) => `${(value * 100).toFixed(1)}%`
const pValue = (value: number) => (value < 0.001 ? '< 0.001' : value.toFixed(3))
const decided = (tally: TurnPreferenceTally) => tally.soniox + tally.chorus
const served = (tally: TurnPreferenceTally) => decided(tally) + tally.no_preference
const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`
const interval = (tally: TurnPreferenceTally) =>
  tally.ci_low === null || tally.ci_high === null ? '—' : `${percent(tally.ci_low)} – ${percent(tally.ci_high)}`

const Card = ({ title, value, detail, tone }: { title: string; value: number; detail: string; tone?: string }) => (
  <div className={styles.card}>
    <p className={styles.cardTitle}>{title}</p>
    <strong className={clsx(styles.big, tone)}>{value}</strong>
    <span className={styles.small}>{detail}</span>
  </div>
)

const SignTest = ({ tally }: { tally: TurnPreferenceTally }) => {
  const n = decided(tally)
  if (n === 0 || tally.win_rate === null || tally.p_value === null || tally.ci_low === null || tally.ci_high === null)
    return <p className={styles.note}>No decided votes yet, so there is nothing to test.</p>
  const inHouseShare = tally.chorus / n
  return (
    <div className={styles.test}>
      <div className={styles.bar} aria-hidden>
        <span className={styles.chorusBar} style={{ width: `${inHouseShare * 100}%` }} />
        <span className={styles.sonioxBar} style={{ width: `${(1 - inHouseShare) * 100}%` }} />
      </div>
      <div className={styles.legend}>
        <span>
          <i className={styles.chorusBar} aria-hidden /> In-house {tally.chorus}
        </span>
        <span>
          <i className={styles.sonioxBar} aria-hidden /> Soniox {tally.soniox}
        </span>
      </div>
      <dl className={styles.facts}>
        <dt>In-house win rate</dt>
        <dd>{percent(tally.win_rate)}</dd>
        <dt>95% Wilson interval</dt>
        <dd>{interval(tally)}</dd>
        <dt>Two-sided exact binomial, n = {n}, null hypothesis p = 0.5</dt>
        <dd>p = {pValue(tally.p_value)}</dd>
      </dl>
      <div className={styles.ci} aria-hidden>
        <i className={styles.axis} />
        <i className={styles.mid} />
        <i
          className={styles.range}
          style={{ left: `${tally.ci_low * 100}%`, width: `${(tally.ci_high - tally.ci_low) * 100}%` }}
        />
        <i className={styles.dot} style={{ left: `${tally.win_rate * 100}%` }} />
        <span className={styles.tick} style={{ left: 0 }}>
          0%
        </span>
        <span className={styles.tick} style={{ left: '50%' }}>
          50%
        </span>
        <span className={styles.tick} style={{ left: '100%' }}>
          100%
        </span>
      </div>
    </div>
  )
}

const Breakdown = ({
  title,
  firstColumn,
  rows,
  labelFor = (label) => label,
  empty,
}: {
  title: string
  firstColumn: string
  rows: TurnPreferenceBreakdownRow[]
  labelFor?: (label: string) => string
  empty: string
}) => (
  <section className={styles.section} aria-label={title}>
    <h2>{title}</h2>
    <div className={styles.tableWrap}>
      <table className={styles.table} aria-label={title}>
        <thead>
          <tr>
            <th scope="col">{firstColumn}</th>
            <th scope="col">In-house</th>
            <th scope="col">Soniox</th>
            <th scope="col">None</th>
            <th scope="col">Win rate</th>
            <th scope="col">95% CI</th>
            <th scope="col">p</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{labelFor(row.label)}</th>
              <td className={styles.chorus}>{row.chorus}</td>
              <td className={styles.soniox}>{row.soniox}</td>
              <td className={styles.none}>{row.no_preference}</td>
              <td>{row.win_rate === null ? '—' : percent(row.win_rate)}</td>
              <td>{interval(row)}</td>
              <td className={styles.p}>{row.p_value === null ? '—' : pValue(row.p_value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <p className={styles.empty}>{empty}</p>}
    </div>
  </section>
)

const TurnPreferenceResultsPage = () => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['attribution-turn-preference-results'],
    queryFn: async ({ signal }) => (await getMeetingAttributionTurnPreferenceResults(signal)).data,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  })

  const download = useMutation({
    mutationFn: exportMeetingAttributionTurnPreferenceVotes,
    onSuccess: ({ data: blob }) =>
      downloadBlob(blob, `turn-preference-votes-${new Date().toISOString().slice(0, 10)}.json`),
  })

  const asOf = data
    ? new Date(data.generated_at).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  return (
    <main className={styles.page}>
      <Helmet>
        <title>Votes: In-house vs. Soniox</title>
      </Helmet>
      <div className={styles.content}>
        <Link className={styles.back} to="/app/attribution">
          ← Back to attribution
        </Link>
        <header className={styles.header}>
          <p className={styles.eyebrow}>Blind turn preference</p>
          <h1>Votes: In-house vs. Soniox</h1>
          <p className={styles.subtitle}>
            Votes from finished comparison sessions in the current round, decoded after the fact. This page shows only
            totals. The download has every vote from every round, with both transcripts for each clip.
          </p>
          {data && (
            <p className={styles.meta}>
              as of {asOf} · {plural(data.finished_sessions, 'finished session')} ·{' '}
              {plural(data.annotators, 'annotator')}
            </p>
          )}
          <div className={styles.actions}>
            <Button
              title="Download votes (JSON)"
              type="light"
              isLoading={download.isPending}
              onClick={() => download.mutate()}
            />
            {download.isError && (
              <span className={styles.warn} role="alert">
                Couldn&apos;t download the votes. Try again.
              </span>
            )}
          </div>
        </header>

        {isLoading && <p className={styles.status}>Loading results…</p>}
        {isError && (
          <p className={styles.status} role="alert">
            Couldn&apos;t load the results.{' '}
            <button type="button" className={styles.retry} onClick={() => void refetch()}>
              Try again
            </button>
          </p>
        )}

        {data && data.finished_sessions === 0 && data.skipped_sessions === 0 && (
          <p className={styles.status}>No finished sessions yet.</p>
        )}

        {data && (data.finished_sessions > 0 || data.skipped_sessions > 0) && (
          <>
            <div className={styles.cards}>
              <Card
                title="In-house preferred"
                value={data.overall.chorus}
                detail={`of ${decided(data.overall)} decided`}
                tone={styles.chorus}
              />
              <Card
                title="Soniox preferred"
                value={data.overall.soniox}
                detail={`of ${decided(data.overall)} decided`}
                tone={styles.soniox}
              />
              <Card
                title="No preference"
                value={data.overall.no_preference}
                detail="served, not voted"
                tone={styles.none}
              />
              <Card
                title="Clips served"
                value={served(data.overall)}
                detail={`across ${plural(data.finished_sessions, 'session')}`}
              />
            </div>

            <section className={styles.section} aria-label="Sign test">
              <h2>
                Sign test <span className={styles.pill}>per vote</span>
              </h2>
              <SignTest tally={data.overall} />
              <p className={styles.note}>
                Each vote counts once. Votes on the same clip from different annotators are not independent, so read the
                p-value as indicative when several people compared the same meeting.
              </p>
            </section>

            <Breakdown
              title="By annotator"
              firstColumn="Annotator"
              rows={data.by_annotator}
              empty="No votes recorded yet."
            />
            <Breakdown
              title="By selection rule"
              firstColumn="Clip picked for"
              rows={data.by_picked_by}
              labelFor={(label) => PICKED_BY_LABELS[label] ?? label}
              empty="No clips served yet."
            />
            <p className={styles.note}>
              &ldquo;Speakers&rdquo; clips are drawn at random from stretches where the models disagree on who spoke,
              and raters judge only the speaker labels. &ldquo;Words&rdquo; clips are drawn at random from stretches
              where the words differ, and raters see the words alone, with speaker labels hidden.
            </p>

            {data.skipped_sessions > 0 && (
              <p className={styles.warn} role="status">
                {plural(data.skipped_sessions, 'finished session')} skipped because the stored snapshot failed
                validation. Those votes are not counted above.
              </p>
            )}
          </>
        )}
      </div>
    </main>
  )
}

export default TurnPreferenceResultsPage
